/**
 * Batch 17-A — External Integrations tab (UI foundation).
 *
 * Provider-agnostic listing of `shop_integrations` rows for the active
 * shop. Reads via the standard supabase client; RLS gates the SELECT to
 * OWNER / MANAGER / ORG_ADMIN active memberships. No plaintext
 * credentials are ever exposed — the UI shows only:
 *
 *   - provider (the enum value, displayed via PROVIDER_LABELS)
 *   - status (`connecting` / `active` / `error` / `disabled`)
 *   - api_key_last4 (the only credential-derived field that's
 *     plaintext-stored — last 4 chars only)
 *   - last_synced_at + last_error_at + last_error_message
 *
 * Provider cards are placeholder-only in this batch. The
 * Shopmonkey-specific connect / configure modal lands in Batch 17-B
 * (or whichever batch introduces the first real adapter).
 *
 * Surface order:
 *   1. Configured integrations: real rows from the table.
 *   2. Available providers: registry-declared provider names that don't
 *      yet have an installed connection. Each is rendered as a disabled
 *      "Coming soon" placeholder card.
 *
 * NEW providers register in the framework via the Postgres enum +
 * adapter registry; once landed, they automatically surface in the
 * "Available" list — no UI edit required.
 */

import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, Plug, AlertTriangle, RefreshCw, ShieldCheck } from 'lucide-react';
import { format } from 'date-fns';

// Provider catalog. Single source of truth for the operator-facing label
// + short description. Keys MUST match the Postgres `integration_provider`
// enum values; mismatches surface as "Unknown" in the listing.
//
// To register a NEW provider for the UI:
//   1. Add the enum value via a migration (ADD VALUE 'tekmetric').
//   2. Add the adapter under supabase/functions/_shared/integrationProviders/.
//   3. Append an entry here. The placeholder card automatically appears.
type ProviderName = 'shopmonkey' | 'tekmetric' | 'shopware' | 'mitchell1' | 'autoleap' | 'napa_tracs' | 'vast' | string;

interface ProviderMeta {
  label: string;
  description: string;
  // True only when the provider has a registered adapter AND the
  // Postgres enum carries its value. Defaults to false in this batch —
  // every provider is "coming soon" until Batch 17-B+.
  available: boolean;
}

const PROVIDER_CATALOG: Record<ProviderName, ProviderMeta> = {
  shopmonkey: {
    label: 'Shopmonkey',
    // Batch 17-B: Shopmonkey is in DISCOVERY MODE. The adapter,
    // testConnection, and runDiscovery endpoints are live but no field
    // mapping / normalization / ingest exists yet. Full onboarding +
    // ingest land in Batch 17-C once authoritative payload samples are
    // captured.
    description: 'Discovery mode (Batch 17-B). Adapter installed; reachable endpoints can be probed read-only. Field mapping and ingest land in a future batch.',
    available: false,
  },
  tekmetric: {
    label: 'Tekmetric',
    description: 'Coming soon. Provider-agnostic framework support is in place; adapter pending.',
    available: false,
  },
  shopware: {
    label: 'Shop-Ware',
    description: 'Coming soon.',
    available: false,
  },
  mitchell1: {
    label: 'Mitchell 1',
    description: 'Coming soon.',
    available: false,
  },
  autoleap: {
    label: 'AutoLeap',
    description: 'Coming soon.',
    available: false,
  },
  napa_tracs: {
    label: 'NAPA TRACS',
    description: 'Coming soon.',
    available: false,
  },
  vast: {
    label: 'Vast',
    description: 'Coming soon. Vast historical CSV import is already available via Setup → Historical Import.',
    available: false,
  },
};

interface ShopIntegrationRow {
  id: string;
  shop_id: string;
  provider: ProviderName;
  status: 'connecting' | 'active' | 'error' | 'disabled';
  api_key_last4: string | null;
  last_synced_at: string | null;
  last_error_at: string | null;
  last_error_message: string | null;
  // Capability blob — provider-agnostic. The UI doesn't render this
  // directly in 17-A but pulls it so adapter-specific UIs can hydrate
  // off the same query later.
  provider_capabilities: Record<string, boolean> | null;
}

const STATUS_BADGE: Record<ShopIntegrationRow['status'], { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  connecting: { label: 'Connecting', variant: 'secondary' },
  active:     { label: 'Active',     variant: 'default' },
  error:      { label: 'Error',      variant: 'destructive' },
  disabled:   { label: 'Disabled',   variant: 'outline' },
};

function providerMeta(provider: ProviderName): ProviderMeta {
  return PROVIDER_CATALOG[provider] ?? {
    label: provider,
    description: 'Unknown provider. Add an entry to PROVIDER_CATALOG to surface a friendlier label.',
    available: false,
  };
}

/**
 * Public tab component. Mount inside `ShopProfile`'s Tabs shell.
 * Read-only in this batch: no connect/disconnect/test actions are
 * wired — the buttons exist as disabled placeholders and link to the
 * future Batch 17-B work.
 */
export function ExternalIntegrationsTab() {
  const { activeShop } = useAuth();

  const { data: rows, isLoading, error, refetch, isRefetching } = useQuery<ShopIntegrationRow[]>({
    queryKey: ['shop-integrations', activeShop?.id],
    enabled: !!activeShop?.id,
    queryFn: async () => {
      if (!activeShop?.id) return [];
      // R3 (Batch 17-A hostile review): clients read the
      // `shop_integrations_safe` view, NOT the base table. The base
      // table grants SELECT to service_role only; the view exposes
      // every column EXCEPT api_key_ciphertext / webhook_secret_ciphertext
      // / webhook_secret_hash. A `select('*')` here is now safe by
      // construction — even a future developer who reaches for `*`
      // cannot leak ciphertext to the client.
      //
      // The cast remains because the generated Supabase types haven't
      // been regenerated for the new view yet; the `ShopIntegrationRow`
      // shape is the runtime contract.
      const { data, error } = await (supabase as unknown as {
        from: (t: string) => {
          select: (cols: string) => {
            eq: (col: string, val: string) => {
              order: (col: string, opts: { ascending: boolean }) => Promise<{ data: unknown; error: { message: string } | null }>;
            };
          };
        };
      })
        .from('shop_integrations_safe')
        .select(
          'id, shop_id, provider, status, api_key_last4, last_synced_at, last_error_at, last_error_message, provider_capabilities',
        )
        .eq('shop_id', activeShop.id)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as ShopIntegrationRow[];
    },
  });

  if (!activeShop?.id) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading integrations…
      </div>
    );
  }

  const configured = rows ?? [];
  // Providers without an installed connection. Render placeholders for
  // every catalog entry except those already in `configured`.
  const installedProviders = new Set(configured.map((r) => r.provider));
  const availableProviders = (Object.keys(PROVIDER_CATALOG) as ProviderName[]).filter(
    (p) => !installedProviders.has(p),
  );

  return (
    <div className="space-y-6">
      {/* Section header card. Mirrors ReminderSettings / BillingPanel
          treatment so the tab reads as a peer of the existing settings
          surfaces. */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plug className="h-5 w-5" />
            External Integrations
          </CardTitle>
          <CardDescription>
            Connect OptiBay to your existing shop management system. Imports stay read-only — your manual OptiBay edits are always preserved.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-3.5 w-3.5 text-green-600" />
            Credentials are encrypted at rest. We display only the last 4 characters of any saved API key.
          </div>
          {error && (
            <div className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-3.5 w-3.5" />
              Failed to load integrations: {(error as Error).message}
            </div>
          )}
          <div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => refetch()}
              disabled={isRefetching}
              className="mt-2"
              data-testid="integrations-refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${isRefetching ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Configured integrations. Empty state explicitly tells the
          operator there are none yet — important because the placeholder
          cards below ARE rendered as Coming soon. */}
      <section className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Configured ({configured.length})
        </h3>
        {configured.length === 0 ? (
          <Card>
            <CardContent className="py-6 text-center text-sm text-muted-foreground">
              No integrations connected yet.
            </CardContent>
          </Card>
        ) : (
          configured.map((row) => {
            const meta = providerMeta(row.provider);
            const badge = STATUS_BADGE[row.status] ?? { label: row.status, variant: 'outline' as const };
            return (
              <Card key={row.id} data-testid={`integration-row-${row.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-base">{meta.label}</CardTitle>
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                  </div>
                  <CardDescription className="text-xs">{meta.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-1 text-xs text-muted-foreground">
                  <div>
                    API key:{' '}
                    {row.api_key_last4 ? (
                      <span className="font-mono">•••• {row.api_key_last4}</span>
                    ) : (
                      <span className="italic">not configured</span>
                    )}
                  </div>
                  <div>
                    Last synced:{' '}
                    {row.last_synced_at ? format(new Date(row.last_synced_at), 'MMM d, yyyy h:mm a') : 'never'}
                  </div>
                  {row.last_error_at && (
                    <div className="text-destructive">
                      Last error{' '}
                      {format(new Date(row.last_error_at), 'MMM d, yyyy h:mm a')}
                      {row.last_error_message ? `: ${row.last_error_message}` : ''}
                    </div>
                  )}
                  <div className="flex gap-2 pt-2 flex-wrap">
                    <Button type="button" size="sm" variant="outline" disabled title="Coming in a future batch">
                      Configure
                    </Button>
                    <Button type="button" size="sm" variant="outline" disabled title="Coming in a future batch">
                      Test connection
                    </Button>
                    {/* Batch 17-B: Run Discovery is a placeholder. The
                        edge function (integration-shopmonkey-discovery)
                        is live and gated on OWNER/MANAGER/ORG_ADMIN.
                        UI wiring lands once credential onboarding ships.
                        Surfacing the disabled button signals capability
                        without exposing partial flows. */}
                    {row.provider === 'shopmonkey' && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled
                        title="Discovery edge function is installed; UI wiring lands once credential onboarding ships"
                      >
                        Run Discovery
                      </Button>
                    )}
                    <Button type="button" size="sm" variant="outline" disabled title="Coming in a future batch">
                      Disconnect
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </section>

      {/* Available providers — registry-driven catalog. */}
      <section className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Available providers
        </h3>
        {availableProviders.map((p) => {
          const meta = providerMeta(p);
          return (
            <Card key={p} data-testid={`integration-available-${p}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-base">{meta.label}</CardTitle>
                  <Badge variant="outline">Coming soon</Badge>
                </div>
                <CardDescription className="text-xs">{meta.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled
                  title="Provider adapter not yet released"
                >
                  Connect
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </section>
    </div>
  );
}
